import type { Lang, Localized as LocalizedValue } from "../types.ts";
import { hasTranslation, pick, resolveMediaLang } from "../catalog.ts";
import { LANGS } from "../i18n/ui.ts";

const HTML_LANG: Record<Lang, string> = {
  de: "de",
  en: "en",
  zh: "zh-Hans",
};

export function htmlLangOf(lang: Lang): string {
  return HTML_LANG[lang];
}

export function langLabel(lang: Lang): string {
  return LANGS.find((l) => l.code === lang)?.label ?? lang;
}

interface TextProps {
  value: LocalizedValue;
  primary: Lang;
  secondary: Lang | null;
  /** Element used for each language block. */
  as?: "p" | "span" | "div" | "h1" | "h2";
  className?: string;
  /** Upstream ships some answers/explanations as HTML with inline images. */
  html?: boolean;
}

/**
 * Renders one piece of question content in the primary language and, when a
 * secondary language is configured, again underneath it.
 *
 * Each block carries its own `lang` so assistive technology switches
 * pronunciation between the two, and untranslated blocks are marked up as
 * fallbacks rather than silently showing German twice.
 */
export function LocalizedText({
  value,
  primary,
  secondary,
  as = "p",
  className,
  html = false,
}: TextProps) {
  const showSecondary =
    secondary !== null &&
    secondary !== primary &&
    // Don't repeat the identical string when the translation is a fallback.
    pick(value, secondary) !== pick(value, primary);

  return (
    <>
      <Block lang={primary} value={value} as={as} className={className} html={html} primary />
      {showSecondary && (
        <Block lang={secondary} value={value} as={as} className={className} html={html} />
      )}
    </>
  );
}

function Block({
  lang, value, as: Tag, className, html, primary = false,
}: {
  lang: Lang;
  value: LocalizedValue;
  as: NonNullable<TextProps["as"]>;
  className?: string;
  html: boolean;
  primary?: boolean;
}) {
  const text = pick(value, lang);
  if (!text) return null;

  const translated = hasTranslation(value, lang);
  // Fallback text is really German, so label it as such for screen readers.
  const effective: Lang = translated ? lang : value.en ? "en" : "de";

  const classes = [
    "loc",
    primary ? "loc--primary" : "loc--secondary",
    !translated && "loc--fallback",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = resolveMediaLang(text, effective);

  return (
    <Tag
      className={classes}
      lang={htmlLangOf(effective)}
      {...(html
        ? { dangerouslySetInnerHTML: { __html: content } }
        : { children: content })}
    />
  );
}
